import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faSpinner, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import type { CommodityPoint, FlexAsset, SwitchGroup } from "./switchGroup";

export interface GroupSelectionListProps {
  points: CommodityPoint[];
  /** Resolver for the group a point would form — used for both preview and locking. */
  groupFor: (key: string) => SwitchGroup | null;
  selectedKey: string | null;
  onToggle: (key: string) => void;
  onClear: () => void;
  loading: boolean;
  portfolioError: string | null;
  /** Per-EAN preswitch state, keyed by stored EAN. */
  pslState: (ean: string) => "idle" | "loading" | "ready" | "error";
  /** Localised blocking reason for a point, or null when it is fine. */
  blockReason: (key: string) => string | null;
  onRetryPreswitch: (ean: string) => void;
  /**
   * True while a group is selected but no destination has been chosen yet.
   *
   * 🔴 ITS MEANING CHANGED with the PSL re-sequencing. The lookup now fires at
   * SELECTION time, so the point-only gates (meter type, `sdpAtriasConfig`,
   * `addPsl.status`, the running-process check) are already answered here — the row
   * is PARTLY assessed, not unassessed. Only the reasons that mention company B
   * (`multipleOwners`, `notForeign`, the config gates) are still outstanding, and
   * the copy this drives says exactly that. It is rendered only when the lookup has
   * landed CLEAN: a loading row shows the spinner, a failed one its Retry, and a
   * point-only refusal its own red reason — never this alongside any of them.
   */
  awaitingDestination: boolean;
  /** User-initiated recovery from a failed portfolio load. */
  onRetryPortfolio: () => void;
}

const directionLabelKey = (directions: CommodityPoint["directions"]): string => {
  if (directions.length === 2) return "customerSwitchDirectionCombined";
  return directions[0] === "injection"
    ? "customerSwitchDirectionInjection"
    : "customerSwitchDirectionOfftake";
};

/**
 * The per-row PreswitchLight spinner.
 *
 * A BLOCK under the row rather than a badge beside the EAN, and it NAMES the check.
 * Since the lookup moved to selection time this is the first thing that happens when
 * a user ticks a point — an unlabelled "Checking…" next to an EAN does not explain a
 * multi-second wait that was not there before, and an Atrias round trip is slow
 * enough to need explaining.
 */
const PreswitchSpinner: React.FC<{ label: string }> = ({ label }) => (
  <span className="mt-2 flex items-center gap-2 text-sm text-primary-400 dark:text-primary-500">
    <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
    {label}
  </span>
);

/**
 * The EAN list for the Manage-tab Customer Switch.
 *
 * ONE group per transfer, so this is a single-selection list rendered as a
 * tickable PARENT with indented locked CHILDREN — never as peers. The children are
 * the sibling connection point (when a flex site links one) and every flex contract
 * that travels with it. They carry the "moves together" badge and are not tickable,
 * mirroring End-of-Contract's linked-row idiom.
 *
 * Every other point is disabled while a group is selected. Deliberately disabled
 * rather than replace-on-click: selecting has a real side effect (the preswitch is a
 * write), the surrounding Manage screens are multi-select so users arrive expecting
 * one, and a disabled row plus one sentence is what teaches the one-group rule.
 */
const GroupSelectionList: React.FC<GroupSelectionListProps> = ({
  points,
  groupFor,
  selectedKey,
  onToggle,
  onClear,
  loading,
  portfolioError,
  pslState,
  blockReason,
  onRetryPreswitch,
  awaitingDestination,
  onRetryPortfolio,
}) => {
  const { t } = useTranslation("management");

  // 🔴 ORDER IS LOAD-BEARING: the ERROR branch comes FIRST.
  //
  // `loading` is passed as `portfolioLoading || portfolioIsStale`, and stale data
  // is not transient — the one-dispatch-per-company pin means nothing refetches on
  // its own. With `loading` checked first, a stale-AND-errored portfolio rendered a
  // permanent spinner and the error branch below was structurally unreachable:
  // localised copy that existed and could never appear, on a page dead for that
  // mount. Ordinary trigger: view one company, switch, its /allservices fails once.
  //
  // Safe because the slice's `pending` reducer CLEARS `error`, so a non-null error
  // implies no fetch is in flight — an in-flight refetch still shows the spinner.
  //
  // A failed load is also NOT an empty portfolio: telling the customer they have no
  // connection points because a request failed says something untrue about their
  // account. That is why this precedes the empty branch too.
  if (portfolioError) {
    return (
      <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex flex-col gap-2">
        <span className="flex gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
          <span>{t("customerSwitchPortfolioError")}</span>
        </span>
        <span>
          <button type="button" className="underline font-semibold" onClick={onRetryPortfolio}>
            {t("customerSwitchRetry")}
          </button>
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-primary-400 py-4">
        <FontAwesomeIcon icon={faSpinner} spin />
        {t("loading")}
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <p className="text-sm text-primary-400 dark:text-primary-500 py-2">
        {t("customerSwitchNoEligible")}
      </p>
    );
  }

  const selectedGroup = selectedKey ? groupFor(selectedKey) : null;
  // Children are rendered under their parent, so they must not also appear as
  // top-level rows.
  const childKeys = new Set<string>(
    selectedGroup?.member ? [selectedGroup.member.key] : []
  );

  const renderFlexChild = (asset: FlexAsset) => (
    <li
      key={asset.key}
      className="flex items-start gap-3 p-2 pl-3 rounded-md border border-gray-200 dark:border-primary-800 opacity-80"
    >
      <input type="checkbox" className="checkbox checkbox-sm mt-1" checked disabled readOnly />
      <span className="flex-1">
        <span className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm font-medium text-primary-600 dark:text-primary-300">
            {asset.name}
          </span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faLink} className="text-[10px]" />
            {t("customerSwitchMovesTogether")}
          </span>
        </span>
        <span className="block text-xs text-primary-400 dark:text-primary-500 mt-0.5">
          {asset.address}
        </span>
      </span>
    </li>
  );

  /**
   * A group MEMBER row — the sibling connection point pulled in by a flex site.
   *
   * 🔴 It must surface its own preswitch state. A member is never `isSelected`, so
   * the parent row's error/Retry branch can never fire for it; and because a failed
   * lookup is now TERMINAL (the `attempted` ref that stopped the request flood), a
   * single Atrias blip on the member would otherwise be permanent and invisible —
   * "Create proposals" greys out via allPreswitchReady with nothing on screen
   * explaining why and no way back short of a reload. Failing closed is right;
   * failing closed SILENTLY is not.
   */
  const renderPointChild = (point: CommodityPoint) => {
    const psl = pslState(point.key);
    return (
      <li
        key={point.key}
        className="flex items-start gap-3 p-2 pl-3 rounded-md border border-gray-200 dark:border-primary-800 opacity-80"
      >
        <input type="checkbox" className="checkbox checkbox-sm mt-1" checked disabled readOnly />
        <span className="flex-1">
          <span className="flex items-center justify-between gap-3 flex-wrap">
            <span className="font-mono text-sm text-primary-600 dark:text-primary-300">
              {point.ean}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 inline-flex items-center gap-1">
                <FontAwesomeIcon icon={faLink} className="text-[10px]" />
                {t("customerSwitchMovesTogether")}
              </span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-300">
                {t(directionLabelKey(point.directions))}
              </span>
            </span>
          </span>
          <span className="block text-xs text-primary-400 dark:text-primary-500 mt-0.5">
            {point.address}
          </span>
          {psl === "loading" && <PreswitchSpinner label={t("customerSwitchPreswitchChecking")} />}
          {psl === "error" && (
            <span className="block mt-2 text-sm text-red-600 dark:text-red-400">
              {t("customerSwitchPreswitchError")}{" "}
              <button
                type="button"
                className="underline font-semibold"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRetryPreswitch(point.key);
                }}
              >
                {t("customerSwitchRetry")}
              </button>
            </span>
          )}
        </span>
      </li>
    );
  };

  return (
    <>
      {selectedKey && (
        <div className="flex items-center justify-between gap-3 mb-3 p-2 rounded-md bg-primary-50 dark:bg-primary-950 text-sm text-primary-600 dark:text-primary-300">
          <span>{t("customerSwitchOneGroupNote")}</span>
          <button
            type="button"
            onClick={onClear}
            className="text-sm font-semibold underline shrink-0"
          >
            {t("customerSwitchClearSelection")}
          </button>
        </div>
      )}
      <ul className="space-y-2">
        {points
          .filter((p) => !childKeys.has(p.key))
          .map((point) => {
            const isSelected = point.key === selectedKey;
            const group = isSelected ? selectedGroup : null;
            const reason = isSelected ? blockReason(point.key) : null;
            const psl = pslState(point.key);
            // Everything that is not the selection is disabled while one exists.
            const disabled = selectedKey !== null && !isSelected;
            return (
              <li key={point.key}>
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? "border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-950"
                      : "border-gray-200 dark:border-primary-800"
                  } ${
                    disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:border-primary-400 dark:hover:border-primary-500"
                  }`}
                >
                  <input
                    type="checkbox"
                    name="customer-switch-selection"
                    className="checkbox checkbox-sm mt-1"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => onToggle(point.key)}
                  />
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-mono text-sm text-primary-600 dark:text-primary-300">
                        {point.ean}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-300">
                          {t(directionLabelKey(point.directions))}
                        </span>
                      </span>
                    </span>
                    <span className="block text-sm text-primary-400 dark:text-primary-500 mt-1">
                      {point.siteName ? `${point.siteName} — ` : ""}
                      {point.address}
                    </span>

                    {isSelected && psl === "loading" && (
                      <PreswitchSpinner label={t("customerSwitchPreswitchChecking")} />
                    )}

                    {isSelected && psl === "error" && (
                      <span className="block mt-2 text-sm text-red-600 dark:text-red-400">
                        {t("customerSwitchPreswitchError")}{" "}
                        <button
                          type="button"
                          className="underline font-semibold"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onRetryPreswitch(point.key);
                          }}
                        >
                          {t("customerSwitchRetry")}
                        </button>
                      </span>
                    )}

                    {/* Only when the lookup landed CLEAN: while it runs the spinner
                        speaks, on failure the Retry does, and a point-only refusal
                        gets the red reason below instead of this. */}
                    {isSelected && awaitingDestination && psl === "ready" && !reason && (
                      <span className="block mt-2 text-sm text-primary-500 dark:text-primary-300">
                        {t("customerSwitchAwaitingDestination")}
                      </span>
                    )}

                    {reason && (
                      <span className="block mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                        {reason}
                      </span>
                    )}

                  </span>
                </label>

                {/* 🔴 OUTSIDE the <label>, deliberately. The member row carries its
                    own Retry BUTTON, and a button nested inside a label has its
                    activation forwarded to the label's control — the click toggles
                    the checkbox (deselecting the group) instead of reaching the
                    handler, so Retry silently does nothing. Keeping the interactive
                    children out of the label is what makes them clickable at all. */}
                {isSelected && group && (group.member || group.flex.length > 0) && (
                  <div className="mt-2 ml-9">
                    <span className="block text-xs text-primary-400 dark:text-primary-500 mb-1">
                      {t("customerSwitchGroupNote")}
                    </span>
                    <ul className="space-y-1">
                      {group.member && renderPointChild(group.member)}
                      {group.flex.map(renderFlexChild)}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    </>
  );
};

export default GroupSelectionList;
